import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  CONDITION_LABELS, FUNDER_KIND_LABELS, OWNER_KIND_LABELS, STATUS_LABELS, WARRANTY_LABELS,
  partyProblem, statusTone, warrantyTone,
} from "../../lib/procurement";
import { hasPermission } from "../../lib/permissions";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${statusTone(status)}`} data-testid={`asset-status-${status}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// « Garantie jusqu'au … » only when it is worth a word: ending or expired.
export function WarrantyNote({ asset }) {
  if (!asset.warranty_state || asset.warranty_state === "valid") return null;
  return <span className={`text-xs ${warrantyTone(asset.warranty_state)}`} data-testid="asset-warranty">{WARRANTY_LABELS[asset.warranty_state]}</span>;
}

export function LoanNote({ asset }) {
  if (!asset.loan) return null;
  return (
    <span className={`text-xs ${asset.loan.overdue ? "text-rose-700 font-medium" : "text-amber-700"}`} data-testid="asset-loan">
      {asset.loan.overdue ? "Retour en retard" : "Retour prévu"} · {asset.loan.expected_return_on.split("-").reverse().join("/")}
    </span>
  );
}

export function ConditionText({ condition }) {
  return <>{CONDITION_LABELS[condition] || condition}</>;
}

export function Field({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-1.5">
      <dt className="text-xs sm:text-sm text-slate-500 sm:w-36 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-900 min-w-0 break-words">{children}</dd>
    </div>
  );
}

// The living collaborator fiches (id and name only) for the owner / funder / holder pickers: GET /procurement/people.
export function usePeople() {
  const [people, setPeople] = useState([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    api.get("/procurement/people").then(({ data }) => setPeople(data.items)).catch(() => setPeople([])).finally(() => setReady(true));
  }, []);
  return { people, ready };
}

// External holders: the pharmacies of the CRM one sees (id, name, commune), searched as one types: GET /procurement/pharmacies.
function PharmacyPicker({ id, label, value, onChange }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (value.prospect_id) return undefined;
    const t = setTimeout(() => {
      api.get("/procurement/pharmacies", { params: { q } }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, value.prospect_id]);
  if (value.prospect_id) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={`party-${id}-pharmacy-chosen`}>
        <span className="min-w-0 break-words">Pharmacie : <strong>{value.name}</strong></span>
        <button type="button" className="shrink-0 text-xs text-slate-500 underline" onClick={() => onChange({ ...value, prospect_id: "", name: "" })}>Changer</button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-10" placeholder="Rechercher une pharmacie du CRM…" aria-label={`${label} : pharmacie du CRM`} data-testid={`party-${id}-pharmacy-search`} />
      {items.length > 0 && (
        <ul className="max-h-44 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid={`party-${id}-pharmacy-list`}>
          {items.map((p) => (
            <li key={p.id}>
              <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => onChange({ ...value, prospect_id: p.id, name: p.name })} data-testid={`party-${id}-pharmacy-${p.id}`}>
                {p.name}{p.commune ? <span className="text-slate-500"> · {p.commune}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-500">Pas dans le CRM ? Saisissez simplement son nom ci-dessous.</p>
    </div>
  );
}

// One « who » choice: a kind (closed list), then the person or the name that kind needs.
export function PartyField({ id, label, hint, value, onChange, kinds, people, kindLabels, withPharmacies = false }) {
  const labels = kindLabels || {};
  const needsPerson = value.kind === "collaborator";
  const needsName = value.kind === "third_party" || value.kind === "external";
  const problem = value.kind ? partyProblem(value, label) : "";
  return (
    <div className="space-y-1.5" data-testid={`party-${id}`}>
      <div className="text-sm font-medium text-slate-700">{label}</div>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
      <Select value={value.kind || undefined} onValueChange={(kind) => onChange({ kind, collaborator_id: "", prospect_id: "", name: "" })}>
        <SelectTrigger className="h-10" aria-label={label} data-testid={`party-${id}-kind`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
        <SelectContent>{kinds.map((k) => <SelectItem key={k} value={k}>{labels[k] || k}</SelectItem>)}</SelectContent>
      </Select>
      {needsPerson && (
        <Select value={value.collaborator_id || undefined} onValueChange={(cid) => onChange({ ...value, collaborator_id: cid })}>
          <SelectTrigger className="h-10" aria-label={`${label} : personne`} data-testid={`party-${id}-person`}><SelectValue placeholder="Choisir la personne…" /></SelectTrigger>
          <SelectContent>
            {people.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">Aucune fiche collaborateur disponible.</div>}
            {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {value.kind === "external" && withPharmacies && <PharmacyPicker id={id} label={label} value={value} onChange={onChange} />}
      {needsName && !value.prospect_id && (
        <Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} maxLength={120} className="h-10"
          placeholder={value.kind === "external" ? "Nom de la personne ou de l'organisation" : "Nom du tiers"} aria-label={`${label} : nom`} data-testid={`party-${id}-name`} />
      )}
      {problem && value.kind && (needsPerson || needsName) && <p className="text-xs text-amber-700">{problem}</p>}
    </div>
  );
}

export const OWNER_KINDS = ["to_confirm", "company", "collaborator", "third_party"];
export const FUNDER_KINDS = ["unknown", "company", "collaborator", "third_party"];
export const HOLDER_KINDS = ["collaborator", "external"];
export const OWNER_LABELS = OWNER_KIND_LABELS;
export const FUNDER_LABELS = FUNDER_KIND_LABELS;

// « Exporter » (CSV of the list the page shows, with what the reader may see): only for whoever holds procurement:export. The server
// journals every download and decides what each column holds.
export function ExportButton({ kind }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user?.features?.procurement || !hasPermission(user, "procurement:export")) return null;
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/procurement/exports/${kind}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `jamora-${kind}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Vous n'avez pas accès à cet export." : "Impossible de télécharger l'export.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button type="button" variant="outline" onClick={download} disabled={busy} data-testid={`export-${kind}`}>
      {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}Exporter
    </Button>
  );
}
